// src/components/logistique/parametrage/ReglagesParc.jsx
// ============================================================================
// Paramétrage de la logistique - le parc : photos exigées, format d'étiquette
// ============================================================================
//
// Régime A de PARAMETRAGE-MODULES.md : chaque réglage part au serveur tout de
// suite, l'écran revient en arrière si le serveur refuse. Écrire demande
// LOGISTIQUE.PARC.MANAGE ; sans elle, la section se lit.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import PorteurNotification from '../../ui/PorteurNotification';
import ListeDeroulante from '../../ui/ListeDeroulante';
import EnTeteSection from '../../parametrage/EnTeteSection';
import CarteReglages from '../../parametrage/CarteReglages';
import ChargementSection from '../../parametrage/ChargementSection';
import { Interrupteur, LigneReglage } from '../../parametrage/PiecesReglage';
import { parc } from '../apiLogistique';
import { BandeauEchec } from '../elementsLogistique';

const ACCENT = '#246990';

const PHOTOS = [
  { cle: 'parc_photo_remise', libelle: 'Photo à la remise', description: "Confier un actif à une personne exige une photo de l'objet : elle prouve son état au moment où il part." },
  { cle: 'parc_photo_retour', libelle: 'Photo au retour', description: "Le retour d'un actif exige une photo : on compare avec celle de la remise." },
  { cle: 'parc_photo_etat', libelle: "Photo au changement d'état", description: 'Une panne, une casse, une réparation exigent une photo de ce qui a changé.' }
];

const FORMATS = [
  { valeur: '70x37', libelle: '24 par planche - 70 x 37 mm' },
  { valeur: '63x38', libelle: '21 par planche - 63,5 x 38,1 mm' }
];

const ReglagesParc = () => {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      setDonnees(await parc.parametrage());
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (erreur && !donnees) {
    return (
      <BandeauEchec onReessayer={charger}>{erreur}</BandeauEchec>
    );
  }
  if (!donnees) return <ChargementSection accent={ACCENT} libelle="Chargement des réglages du parc…" />;
  const gerer = Boolean(donnees.droits?.gerer);
  const r = donnees.reglages;

  // Régime A : l'état local d'abord, le serveur ensuite, le retour en arrière s'il refuse.
  const changer = async (cle, valeur) => {
    const avant = r[cle];
    setDonnees((d) => ({ ...d, reglages: { ...d.reglages, [cle]: valeur } }));
    setEnCours(cle);
    try {
      const reglages = await parc.enregistrerParametrage({ [cle]: valeur });
      setDonnees((d) => ({ ...d, reglages }));
      setToast({ message: 'Configuration mise à jour', type: 'success' });
    } catch (e) {
      setDonnees((d) => ({ ...d, reglages: { ...d.reglages, [cle]: avant } }));
      setToast({ message: e.message, type: 'error' });
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="space-y-5">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={Camera}
        titre="Les gestes du parc"
        sousTitre={gerer ? 'Ce que les fenêtres de remise, de retour et de changement d\'état exigent' : 'Lecture seule : il faut la permission de gérer le parc pour changer ces réglages'}
        accent={ACCENT}
        accentSombre="#1a4f6e"
      />
      <CarteReglages titre="Photos exigées" accent={ACCENT} compteur={`${PHOTOS.filter((p) => r[p.cle]).length} sur ${PHOTOS.length}`} dense>
        {PHOTOS.map((p) => (
          <LigneReglage key={p.cle} actif={r[p.cle]} accent={ACCENT} libelle={p.libelle} description={p.description}>
            <Interrupteur actif={r[p.cle]} accent={ACCENT} libelle={p.libelle} desactive={!gerer} enCours={enCours === p.cle} onBasculer={() => changer(p.cle, !r[p.cle])} />
          </LigneReglage>
        ))}
      </CarteReglages>
      <CarteReglages titre="Étiquettes" accent={ACCENT} dense>
        <LigneReglage accent={ACCENT} libelle="Format proposé à l'impression" description="La planche d'étiquettes autocollantes choisie d'avance dans la fenêtre d'impression ; on peut toujours en changer au moment d'imprimer.">
          <div className="w-64">
            <ListeDeroulante
              aria-label="Format d'étiquette"
              valeur={r.parc_format_etiquette}
              desactive={!gerer || enCours === 'parc_format_etiquette'}
              onChange={(v) => changer('parc_format_etiquette', v)}
              options={FORMATS}
            />
          </div>
        </LigneReglage>
      </CarteReglages>
    </div>
  );
};

export default ReglagesParc;
